/**
 * Roles API Service (Enhanced)
 * API calls for role and RBAC management
 * Supports custom role creation, editing, cloning
 */
import { apiRequest } from './adminApi';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper for direct API calls with token
const directApiCall = async (endpoint, options = {}) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || 'Request failed');
  }
  
  return response.json();
};

/**
 * List all roles (system + custom)
 */
export const listRoles = async () => {
  try {
    const data = await directApiCall('/api/roles/');
    return data.roles || [];
  } catch (e) {
    // Fallback to legacy endpoint
    return await apiRequest('/roles');
  }
};

/**
 * List all available permissions
 * Converts object format to array format for PermissionMatrix compatibility
 */
export const listPermissions = async () => {
  try {
    const data = await directApiCall('/api/roles/permissions');
    const permissionsObj = data.permissions || {};
    
    // Convert object format to array format
    // {resource: {label, actions: [...]}} -> [{resource, action, label}]
    const permissionsArray = [];
    Object.entries(permissionsObj).forEach(([resource, config]) => {
      (config.actions || []).forEach(action => {
        permissionsArray.push({
          resource,
          action,
          label: config.label,
          description: config.description
        });
      });
    });
    
    return permissionsArray;
  } catch (e) {
    // Fallback to legacy endpoint
    return await apiRequest('/permissions');
  }
};

/**
 * Get available permissions for role creation
 */
export const getAvailablePermissions = async () => {
  const data = await directApiCall('/api/roles/permissions');
  return data;
};

/**
 * Get RBAC matrix (roles × permissions)
 */
export const getRBACMatrix = async () => {
  return await apiRequest('/rbac/matrix');
};

/**
 * Get a specific role with permissions
 */
export const getRole = async (roleId) => {
  return await directApiCall(`/api/roles/${roleId}`);
};

/**
 * Get role's permissions
 */
export const getRolePermissions = async (roleId) => {
  try {
    const role = await directApiCall(`/api/roles/${roleId}`);
    return role?.permissions || [];
  } catch (e) {
    const roles = await listRoles();
    const role = roles.find(r => r.id === roleId);
    return role?.permissions || [];
  }
};

/**
 * Update role's permissions
 */
export const updateRolePermissions = async (roleId, permissions) => {
  return await directApiCall(`/api/roles/${roleId}`, {
    method: 'PUT',
    body: JSON.stringify({ permissions })
  });
};

/**
 * Create a new custom role
 */
export const createRole = async (roleData) => {
  return await directApiCall('/api/roles/', {
    method: 'POST',
    body: JSON.stringify(roleData)
  });
};

/**
 * Update a custom role
 */
export const updateRole = async (roleId, roleData) => {
  return await directApiCall(`/api/roles/${roleId}`, {
    method: 'PUT',
    body: JSON.stringify(roleData)
  });
};

/**
 * Clone an existing role
 */
export const cloneRole = async (sourceRoleId, newName, newDescription) => {
  return await directApiCall('/api/roles/clone', {
    method: 'POST',
    body: JSON.stringify({
      source_role_id: sourceRoleId,
      new_name: newName,
      new_description: newDescription
    })
  });
};

/**
 * Delete a custom role
 */
export const deleteRole = async (roleId) => {
  return await directApiCall(`/api/roles/${roleId}`, {
    method: 'DELETE'
  });
};

/**
 * Get users for a role
 */
export const getRoleUsers = async (roleId, page = 1, limit = 20) => {
  const data = await directApiCall(`/api/roles/${roleId}/users?page=${page}&limit=${limit}`);
  return data;
};

// ============================================================================
// ADMIN RBAC MATRIX ENDPOINTS (DB-driven enforcement)
// ============================================================================

/**
 * List roles from the admin RBAC system (with resolved permissions)
 */
export const getAdminRoles = async () => {
  return await directApiCall('/api/admin/roles');
};

/**
 * List all available permissions from the admin RBAC system
 */
export const getAdminPermissions = async () => {
  return await directApiCall('/api/admin/permissions');
};

/**
 * Save permission matrix changes (replaces all permissions for specified roles)
 * @param {Array} grants - [{role: "Admin", resource: "users", action: "view"}, ...]
 */
export const saveMatrixPermissions = async (grants) => {
  return await directApiCall('/api/admin/rbac/matrix', {
    method: 'PUT',
    body: JSON.stringify({ grants })
  });
};

/**
 * Reset a system role's permissions to hardcoded defaults
 * @param {string} roleKey - Role name (e.g. "Admin", "Team Leader")
 */
export const resetRoleDefaults = async (roleKey) => {
  return await directApiCall(`/api/admin/rbac/roles/${encodeURIComponent(roleKey)}/reset-defaults`, {
    method: 'POST'
  });
};
